import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Search, Wallet, CreditCard, ShieldCheck, Building2, TrendingUp, LineChart } from "lucide-react";
import api, { formatINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatPayout } from "@/lib/payout";
import { Input } from "@/components/ui/input";
import { getCategoryMeta } from "@/lib/categoryMeta";
import { ProductImage } from "@/components/ProductImage";

// Top-level groups; each maps to a list of categories (sub-tabs).
// Single-item groups (Credit Cards, Savings) skip sub-tabs and show products directly.
const GROUPS = [
  { id: "loans", label: "Loans", icon: Wallet, color: "from-emerald-900 to-emerald-800",
    categories: ["Personal Loans", "Business Loans", "Home Loans", "Loan Against Property"] },
  { id: "insurance", label: "Insurance", icon: ShieldCheck, color: "from-amber-700 to-amber-600",
    categories: ["Life Insurance", "Term Insurance", "Health Insurance", "Motor Insurance", "Travel Insurance", "Home Insurance"] },
  { id: "cards", label: "Credit Cards", icon: CreditCard, color: "from-stone-800 to-stone-700",
    categories: ["Credit Cards"] },
  { id: "savings", label: "Savings Accounts", icon: Building2, color: "from-emerald-700 to-emerald-600",
    categories: ["Savings Accounts"] },
  { id: "demat", label: "Demat Accounts", icon: LineChart, color: "from-amber-600 to-amber-500",
    categories: ["Demat Accounts"] },
];

function categoryToGroup(category) {
  return GROUPS.find((g) => g.categories.includes(category));
}

export default function Products() {
  const { user: authUser } = useAuth();
  const hideMoney = !!authUser?.corporate_id;
  const [products, setProducts] = useState([]);
  const [groupId, setGroupId] = useState(null);
  const [cat, setCat] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      try {
        const r = await api.get("/products");
        if (!cancelled) setProducts(r.data);
      } catch { /* ignore — keep stale data on transient failure */ void 0; }
    };
    fetch();
    // Auto-refresh every 60s while the tab is visible — picks up admin edits live.
    const interval = setInterval(() => { if (document.visibilityState === "visible") fetch(); }, 60_000);
    // Refresh immediately when the tab regains focus (most common "I just edited in admin" path).
    const onVisible = () => { if (document.visibilityState === "visible") fetch(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, []);

  // Count products per group/cat from real data
  const groupCounts = useMemo(() => {
    const out = {};
    GROUPS.forEach((g) => { out[g.id] = products.filter((p) => g.categories.includes(p.category)).length; });
    // also collect any uncategorized products into "other"
    const known = new Set(GROUPS.flatMap((g) => g.categories));
    out._other = products.filter((p) => p.category && !known.has(p.category)).length;
    return out;
  }, [products]);

  const otherCategories = useMemo(() => {
    const known = new Set(GROUPS.flatMap((g) => g.categories));
    return Array.from(new Set(products.map((p) => p.category).filter((c) => c && !known.has(c))));
  }, [products]);

  const currentGroup = groupId === "_other"
    ? { id: "_other", label: "More", categories: otherCategories }
    : GROUPS.find((g) => g.id === groupId);

  const filtered = useMemo(() => {
    let list = products;
    if (currentGroup) {
      if (currentGroup.categories.length === 1) {
        list = list.filter((p) => p.category === currentGroup.categories[0]);
      } else if (cat) {
        list = list.filter((p) => p.category === cat);
      } else {
        list = list.filter((p) => currentGroup.categories.includes(p.category));
      }
    }
    if (q) {
      const needle = q.toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(needle) || (p.bank || "").toLowerCase().includes(needle));
    }
    return list;
  }, [products, currentGroup, cat, q]);

  // No group selected → group landing
  if (!groupId) {
    return (
      <div className="space-y-8" data-testid="products-page">
        <div>
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-800">Catalog</span>
          <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tight mt-2">Recommend & Earn</h1>
          <p className="text-stone-600 mt-2">Pick a category to see all products and start sharing.</p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <Link to="/app/claims" data-testid="claims-management-card"
            className="group relative overflow-hidden sm:col-span-2 lg:col-span-3 bg-gradient-to-br from-emerald-950 to-emerald-900 text-white rounded-2xl p-7 hover:shadow-2xl hover:-translate-y-0.5 transition-all block">
            <div className="absolute -top-8 -right-8 w-40 h-40 bg-amber-400/10 rounded-full blur-2xl" />
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-amber-300">New · Claim Assistance</span>
                <div className="font-display text-3xl font-black tracking-tight mt-1">Claims Management</div>
                <p className="text-sm text-white/75 mt-1 max-w-xl">Get expert assistance for Life, Health, Motor, Fire, Marine and other insurance claims through verified claim specialists.</p>
              </div>
              <span className="inline-flex items-center gap-2 bg-amber-400 text-emerald-950 font-black text-sm px-5 py-2.5 rounded-full group-hover:gap-3 transition-all">
                Raise Claim Request <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </Link>
          {GROUPS.map((g) => {
            const Icon = g.icon;
            const count = groupCounts[g.id] || 0;
            return (
              <button key={g.id} onClick={() => { setGroupId(g.id); setCat(null); }}
                data-testid={`group-${g.id}`}
                className={`group relative overflow-hidden text-left bg-gradient-to-br ${g.color} text-white rounded-2xl p-7 hover:shadow-2xl hover:-translate-y-0.5 transition-all ${count === 0 ? "opacity-60" : ""}`}>
                <div className="absolute -top-8 -right-8 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
                <Icon className="w-8 h-8 mb-4 opacity-90" />
                <div className="font-display text-3xl font-black tracking-tight">{g.label}</div>
                <div className="text-sm text-white/80 mt-1">{count > 0 ? `${count} product${count !== 1 ? "s" : ""}` : "Coming soon"}</div>
                {g.categories.length > 1 && (
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {g.categories.slice(0, 3).map((c) => (
                      <span key={c} className="bg-white/15 backdrop-blur text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full">{c.replace(" Insurance", "").replace(" Loans", "").replace(" Loan", "")}</span>
                    ))}
                    {g.categories.length > 3 && <span className="text-[10px] font-bold text-white/70">+{g.categories.length - 3} more</span>}
                  </div>
                )}
                <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  <ArrowRight className="w-5 h-5" />
                </div>
              </button>
            );
          })}
          {groupCounts._other > 0 && (
            <button onClick={() => { setGroupId("_other"); setCat(null); }} data-testid="group-other"
              className="text-left bg-stone-100 border-2 border-dashed border-stone-300 rounded-2xl p-7 hover:border-emerald-900 transition-all">
              <TrendingUp className="w-8 h-8 mb-4 text-stone-600" />
              <div className="font-display text-2xl font-black">More</div>
              <div className="text-sm text-stone-600 mt-1">{groupCounts._other} other products</div>
            </button>
          )}
        </div>
      </div>
    );
  }

  // Inside a group
  const GroupIcon = currentGroup.icon || TrendingUp;
  return (
    <div className="space-y-8" data-testid="products-page">
      <div>
        <button onClick={() => { setGroupId(null); setCat(null); }} data-testid="back-to-groups"
          className="inline-flex items-center gap-1.5 text-sm font-bold text-stone-700 hover:text-emerald-900 mb-3">
          <ArrowLeft className="w-3.5 h-3.5" /> All categories
        </button>
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-xl ${currentGroup.color ? `bg-gradient-to-br ${currentGroup.color}` : "bg-emerald-900"} text-white grid place-items-center`}>
            <GroupIcon className="w-6 h-6" />
          </div>
          <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tight">{currentGroup.label}</h1>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-3 w-4 h-4 text-stone-400" />
          <Input data-testid="product-search" placeholder="Search by name or bank..." value={q} onChange={(e) => setQ(e.target.value)}
            className="pl-10 h-11 rounded-full border-stone-300" />
        </div>
      </div>

      {currentGroup.categories.length > 1 && (
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setCat(null)} data-testid="subcat-all"
            className={`px-4 py-2 rounded-full text-xs font-bold transition-colors ${
              !cat ? "bg-emerald-900 text-white" : "bg-white border border-stone-300 text-stone-700 hover:border-emerald-900"
            }`}>All</button>
          {currentGroup.categories.map((c) => (
            <button key={c} data-testid={`subcat-${c.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => setCat(c)}
              className={`px-4 py-2 rounded-full text-xs font-bold transition-colors ${
                cat === c ? "bg-emerald-900 text-white" : "bg-white border border-stone-300 text-stone-700 hover:border-emerald-900"
              }`}>{c}</button>
          ))}
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {filtered.map((p) => {
          const meta = getCategoryMeta(p.category);
          const Icon = meta.icon;
          return (
            <Link to={`/app/products/${p.id}`} key={p.id} data-testid={`product-${p.id}`}
              className="group bg-white rounded-2xl border border-stone-200 overflow-hidden hover:shadow-md transition-all">
              <div className="relative">
                <ProductImage product={p} className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className={`absolute top-3 left-3 ${meta.bg} ${meta.text} w-11 h-11 rounded-xl grid place-items-center shadow-lg`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className={`absolute top-3 right-3 ${meta.badge} text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full`}>
                  {p.category}
                </div>
                {p.poster_meta && (
                  <div className="absolute bottom-2 left-2 bg-emerald-900 text-amber-300 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full shadow-md inline-flex items-center gap-1" data-testid={`product-${p.id}-poster-badge`}>
                    <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>
                    Poster
                  </div>
                )}
              </div>
              <div className="p-6">
                <h3 className="font-display text-xl font-bold tracking-tight">{p.name}</h3>
                <p className="text-sm text-stone-500 mt-1">{p.bank}</p>
                <div className="mt-5 pt-5 border-t border-stone-100 flex items-center justify-between">
                  {!hideMoney && <div>
                    <div className="text-xs text-stone-500">Payout</div>
                    <div className="font-display font-black text-emerald-900">{formatPayout(p)}</div>
                  </div>}
                  <ArrowRight className="w-5 h-5 text-stone-400 group-hover:text-emerald-900 group-hover:translate-x-1 transition-all" />
                </div>
              </div>
            </Link>
          );
        })}
      </div>
      {filtered.length === 0 && <div className="text-center py-16 text-stone-500">No products match.</div>}
    </div>
  );
}

// keep icon reference (used by Group items)
// eslint-disable-next-line no-unused-vars
const _icons = { GroupIcon: TrendingUp };

// Re-attach icons to GROUPS at module load (closure issue in arrow init order)
GROUPS[0].icon = Wallet;
GROUPS[1].icon = ShieldCheck;
GROUPS[2].icon = CreditCard;
GROUPS[3].icon = Building2;
GROUPS[4].icon = LineChart;
// Used to mark `categoryToGroup` as referenced (helper for future grouping logic)
void categoryToGroup;
